import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Check, Plus, Upload, MessageSquare, Table, LayoutGrid, Edit, UserPlus, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';

const OnboardingTutorial = ({ onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const steps = [
    {
      title: "Welcome to I-Match CRM! 👋",
      description: "Let's take a quick tour to get you started with managing your leads",
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            I-Match is an <strong>AI-powered Lead Enrichment CRM</strong> that helps you manage, 
            enrich, and convert leads efficiently.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">What you'll learn:</h4>
            <ul className="space-y-2 text-blue-800">
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-blue-600" />
                How to add leads (3 different ways)
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-blue-600" />
                How to view and organize your leads
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-blue-600" />
                How to manage and update lead information
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-blue-600" />
                How to edit lead details
              </li>
            </ul>
          </div>
          <p className="text-sm text-gray-600">
            This will only take 2-3 minutes. You can skip or replay this tutorial anytime.
          </p>
        </div>
      ),
      highlight: null
    },
    {
      title: "Adding Leads - 3 Easy Ways 📝",
      description: "Learn how to add leads to your CRM",
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            I-Match offers <strong>three convenient ways</strong> to add leads to your CRM:
          </p>
          
          <div className="space-y-3">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Plus className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">1. Manual Entry</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Click the <strong>"Add Lead"</strong> button in the top right corner to manually enter lead details.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Upload className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">2. CSV Import</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Navigate to <strong>"Import"</strong> in the navigation menu to upload multiple leads at once via CSV file.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <MessageSquare className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">3. AI Chatbot</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Use the <strong>chatbot</strong> in the bottom right corner to add leads conversationally using AI.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-900">
              <strong>💡 Pro Tip:</strong> All newly added leads are automatically enriched with AI to fill in missing information!
            </p>
          </div>
        </div>
      ),
      highlight: 'add-lead-button'
    },
    {
      title: "Viewing Your Leads 👀",
      description: "Choose between Table View and Kanban View",
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            I-Match provides <strong>two views</strong> to visualize your leads:
          </p>
          
          <div className="space-y-3">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <Table className="h-5 w-5 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">Table View (Default)</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    See all your leads in a <strong>detailed table format</strong> with columns for name, company, 
                    stage, status, score, and owner. Perfect for bulk management and quick scanning.
                  </p>
                  <ul className="mt-2 text-sm text-gray-500 space-y-1">
                    <li>• Search and filter leads</li>
                    <li>• Sort by any column</li>
                    <li>• Quick actions (email, call, edit)</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-pink-100 rounded-lg">
                  <LayoutGrid className="h-5 w-5 text-pink-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">Kanban View</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Visualize your sales pipeline with <strong>drag-and-drop cards</strong> organized by lead stage. 
                    Perfect for tracking progress visually.
                  </p>
                  <ul className="mt-2 text-sm text-gray-500 space-y-1">
                    <li>• Drag leads between stages</li>
                    <li>• See owner avatars on cards</li>
                    <li>• Visual pipeline overview</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-900">
              <strong>Toggle between views:</strong> Look for the "Table View" / "Kanban View" button at the top of the Leads page.
            </p>
          </div>
        </div>
      ),
      highlight: 'view-toggle'
    },
    {
      title: "Managing Leads 🔄",
      description: "Update stages, status, and assign owners",
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Keep your leads organized with these <strong>quick management actions</strong>:
          </p>
          
          <div className="space-y-3">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <RefreshCw className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Change Lead Stage</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Use the <strong>Stage dropdown</strong> in the table to move leads through your pipeline:
                    <span className="block mt-1 text-xs text-gray-500">
                      New → Contacted → Qualified → Proposal → Negotiation → Won/Lost
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Check className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Update Status</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Mark leads as <strong>Active</strong>, <strong>Cold</strong>, or <strong>Inactive</strong> to prioritize your outreach.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <UserPlus className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Assign Owner</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Use the <strong>Owner dropdown</strong> to assign leads to team members. Great for team collaboration!
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-sm text-green-900">
              <strong>✨ All changes are saved automatically</strong> and team members are notified when assigned.
            </p>
          </div>
        </div>
      ),
      highlight: 'lead-actions'
    },
    {
      title: "Editing Lead Details ✏️",
      description: "View and update complete lead information",
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Access detailed lead information and perform advanced actions:
          </p>
          
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Edit className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900">Open Lead Details Page</h4>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Click on any lead's name</strong> in the table to open their detailed page.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">On the Lead Details Page, you can:</h4>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <Check className="h-4 w-4 text-blue-600 mt-0.5" />
                <span><strong>Edit lead information</strong> - Update name, email, phone, company</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-4 w-4 text-blue-600 mt-0.5" />
                <span><strong>AI Enrichment</strong> - Click "Enrich with AI" to auto-fill missing data</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-4 w-4 text-blue-600 mt-0.5" />
                <span><strong>Add internal notes</strong> - Collaborate with team via activity logs</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-4 w-4 text-blue-600 mt-0.5" />
                <span><strong>Create tasks</strong> - Set reminders and to-dos for this lead</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-4 w-4 text-blue-600 mt-0.5" />
                <span><strong>Upload files</strong> - Attach documents, contracts, proposals</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-4 w-4 text-blue-600 mt-0.5" />
                <span><strong>Chat with AI</strong> - Get insights about the lead via chatbot</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
            <p className="text-sm text-purple-900">
              <strong>💡 Pro Tip:</strong> Use the "Quick Actions" section for fast access to common tasks!
            </p>
          </div>
        </div>
      ),
      highlight: 'lead-name'
    },
    {
      title: "You're All Set! 🎉",
      description: "Start managing your leads like a pro",
      content: (
        <div className="space-y-4">
          <div className="text-center py-4">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
              <Check className="h-10 w-10 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Congratulations!</h3>
            <p className="text-gray-600">
              You now know the essentials of I-Match CRM
            </p>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">Next Steps:</h4>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">1</div>
                <span>Add your first lead using one of the three methods</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">2</div>
                <span>Explore the Companies and Contacts pages</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">3</div>
                <span>Set up your first task and calendar event</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">4</div>
                <span>Invite team members via Admin Panel</span>
              </li>
            </ul>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-900 text-center">
              <strong>Need help?</strong> Click the <strong>Help (❓)</strong> button in the header to replay this tutorial anytime.
            </p>
          </div>
        </div>
      ),
      highlight: null
    }
  ];

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    onComplete(dontShowAgain);
  };

  const handleSkip = () => {
    onSkip(dontShowAgain);
  };

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        {/* Modal */}
        <Card className="w-full max-w-2xl max-h-[90vh] overflow-auto shadow-2xl">
          <CardHeader className="border-b border-gray-200 pb-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <CardTitle className="text-2xl font-bold text-gray-900">
                  {currentStepData.title}
                </CardTitle>
                <CardDescription className="text-gray-600 mt-1">
                  {currentStepData.description}
                </CardDescription>
              </div>
              <button
                onClick={handleSkip}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Skip tutorial"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {/* Progress Indicator */}
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                <span>Step {currentStep + 1} of {steps.length}</span>
                <span>{Math.round(((currentStep + 1) / steps.length) * 100)}% Complete</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                />
              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-6">
            {/* Step Content */}
            <div className="mb-6">
              {currentStepData.content}
            </div>

            {/* Don't Show Again - Only on last step */}
            {isLastStep && (
              <div className="mb-6 flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Checkbox
                  id="dontShowAgain"
                  checked={dontShowAgain}
                  onCheckedChange={setDontShowAgain}
                />
                <label
                  htmlFor="dontShowAgain"
                  className="text-sm text-gray-700 cursor-pointer"
                >
                  Don't show this tutorial again
                </label>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between">
              <Button
                onClick={handleSkip}
                variant="ghost"
                className="text-gray-600 hover:text-gray-900"
              >
                Skip Tutorial
              </Button>

              <div className="flex items-center gap-2">
                {!isFirstStep && (
                  <Button
                    onClick={handlePrevious}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                )}
                
                <Button
                  onClick={handleNext}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                >
                  {isLastStep ? (
                    <>
                      Get Started
                      <Check className="h-4 w-4" />
                    </>
                  ) : (
                    <>
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default OnboardingTutorial;
